import React, { useState, useEffect, useCallback } from "react";
import FormInput from "../../../../form-input/form-input.component";
import resetPasswordForm from "../../../../../../pages/pre-auth/reset-password/forms/reset-password.forms";
import formValidityCheck from "../../../../../utils/validator";
import AppService from "../../../../../services/app-service";
import { ucsfLoginInstance } from "../../../../../axios/ucsf";
import { COMPONENT_APIS } from "../../../../../utils/omi-api";
import { setLoader } from "../../../../../../redux/app/app.actions";
import { useDispatch } from "react-redux";

const ResetPasswordFlow = ({ onViewChange, urlParams = {} }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    resetFormData: { ...resetPasswordForm },
    formIsValid: false,
    loading: false,
  });
  const [tokenValid, setTokenValid] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const token = urlParams.token || "";

  const validateToken = useCallback(async () => {
    dispatch(setLoader(true, "Validating reset link..."));
    try {
      await ucsfLoginInstance.post(COMPONENT_APIS.CHECK_EXPIRY_RESET_PWD, {
        token: token,
      });
      setTokenValid(true);
      dispatch(setLoader(false));
    } catch (error) {
      setTokenValid(false);
      setErrorMessage(
        error.response?.data?.message ||
          "This reset link has expired or is invalid.",
      );
      dispatch(setLoader(false));
    }
  }, [token, dispatch]);

  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token, validateToken]);

  const inputChangedHandler = (event, identifier) => {
    let formIsValid = true;
    const updatedResetForm = { ...formData.resetFormData };
    const updatedFormElement = { ...updatedResetForm[identifier] };
    updatedFormElement.value = event.target.value;
    const { validity, errorMessage } = formValidityCheck(
      updatedFormElement,
      updatedResetForm,
    );
    updatedFormElement.valid = validity;
    if (updatedFormElement.validation)
      updatedFormElement.validation.message = errorMessage;
    updatedFormElement.touched = true;
    updatedResetForm[identifier] = updatedFormElement;
    for (let id in updatedResetForm) {
      formIsValid = updatedResetForm[id].valid && formIsValid;
    }
    setFormData({ ...formData, resetFormData: updatedResetForm, formIsValid });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    dispatch(setLoader(true, "Resetting password..."));

    try {
      await ucsfLoginInstance.post(COMPONENT_APIS.RESET_PASSWORD_API, {
        token: token,
        password: AppService.encryptData(formData.resetFormData.password.value),
      });

      AppService.setNotification({
        title: "Success!",
        message:
          "Your password has been reset successfully. Please log in with your new password.",
      });

      setResetSuccess(true);
      dispatch(setLoader(false));

      setTimeout(() => {
        onViewChange("login");
      }, 1500);
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
          "Failed to reset password. Please try again.",
      );
      dispatch(setLoader(false));
    }
  };

  if (tokenValid === null) {
    return (
      <div className="auth-card-header">
        <p className="auth-card-subtitle">Validating reset link...</p>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <>
        <div className="auth-card-header">
          <h2 className="auth-card-title">Invalid Link</h2>
        </div>
        <div className="auth-error-message">{errorMessage}</div>
        <div className="auth-card-footer-links" style={{ marginTop: "1rem" }}>
          <button
            className="auth-card-link"
            onClick={() => onViewChange("login")}
          >
            Back to Login
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="auth-card-header">
        <h2 className="auth-card-title">Reset Password</h2>
        <p className="auth-card-subtitle">Enter your new password</p>
      </div>

      {!resetSuccess && (
        <form onSubmit={handleSubmit} className="auth-card-form">
          <div className="auth-card-content">
            <div className="form-group">
              <label className="form-label">New Password</label>
              <FormInput
                elementType={formData.resetFormData.password.elementType}
                elementConfig={formData.resetFormData.password.elementConfig}
                value={formData.resetFormData.password.value}
                changed={(event) => inputChangedHandler(event, "password")}
                touched={formData.resetFormData.password.touched}
                invalid={!formData.resetFormData.password.valid}
                shouldValidate={formData.resetFormData.password.validation}
                errorMessage={
                  formData.resetFormData.password.validation?.message
                }
              />
            </div>

            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <FormInput
                elementType={formData.resetFormData.confirmPassword.elementType}
                elementConfig={
                  formData.resetFormData.confirmPassword.elementConfig
                }
                value={formData.resetFormData.confirmPassword.value}
                changed={(event) =>
                  inputChangedHandler(event, "confirmPassword")
                }
                touched={formData.resetFormData.confirmPassword.touched}
                invalid={!formData.resetFormData.confirmPassword.valid}
                shouldValidate={
                  formData.resetFormData.confirmPassword.validation
                }
                errorMessage={
                  formData.resetFormData.confirmPassword.validation?.message
                }
              />
            </div>

            <div className="auth-card-button-group">

              <button
                className="auth-card-button primary"
                onClick={handleSubmit}
                type="submit"
                disabled={!formData.formIsValid || formData.loading}
              >
                Reset Password
              </button>
            </div>
          </div>
        </form>
      )}

      {resetSuccess && (
        <div className="auth-success-message">
          Your password has been reset successfully! Redirecting to login...
        </div>
      )}
    </>
  );
};

export default ResetPasswordFlow;
